import "./style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { InputLabel } from "@mui/material";
import AlertTriangle from "assets/img/svg/alertTriangle";
import ChannelCession from "assets/img/svg/ChannelCession";
import ChannelCollective from "assets/img/svg/ChannelCollective";
import ChannelNumerique from "assets/img/svg/ChannelNumerique";
import ChannelOther from "assets/img/svg/ChannelOther";
import ChannelPhysique from "assets/img/svg/ChannelPhysique";
import { DocumentOutline } from "assets/img/svg/documentOutline";
import { DownloadOutline } from "assets/img/svg/downloadOutline";
import Beneficiaries from "assets/img/svg/navbar/beneficiaries";
import Book from "assets/img/svg/navbar/book";
import Channels from "assets/img/svg/navbar/channels";
import classNames from "classnames";
import ImportModalButton from "components/ImportData/ImportModalButton";
import { Pending } from "components/Management/ModalContent";
import DocumentationLink from "components/shared/DocumentationLink";
import AutocompleteFormComponent, {
    renderChannelsOption,
} from "components/shared/FormComponent/AutocompleteFormComponent";
import MappingColumns from "components/shared/MappingColumns";
import RepairingTable from "components/shared/RepairingTable";
import { Spinner } from "components/shared/Spinner";
import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import { formatDataFromFile, parseCsvWithHeader, type ParsedCsvFile } from "containers/BulkUpdatesContainer/utils";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import { getWorksListParams } from "containers/Works/worksListParams";
import { formatPayloadMegabytes } from "helpers/jsonPayloadLimit";
import { PortalType } from "contexts/portal.context";
import { canAddSalesForKind } from "helpers/channelCapabilities";
import { createEncodedCSVString } from "helpers/importData-helpers";
import { resolvePressChannelSlot } from "helpers/pressCalculationBase";
import { formatLocaleDateString, getCleanStartorEndDay, isObjectEmpty } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { SyntheticEvent } from "react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { fetchAllAdvanceSlices } from "store/advancePaymentSlice";
import { exportBeneficiaryList, fetchBeneficiaries, getBeneficiaryTags } from "store/beneficiaryStore";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import { exportChannelList, fetchChannels } from "store/channelStore";
import type { ICustomField } from "store/customFieldsStore";
import type { TImportDataSaleMetadata } from "store/dataImportSlice";
import {
    bulkUpdateTermPeriodBalance,
    clearErrors,
    fetchImportAdvancePayments,
    fetchImportArticles,
    fetchImportBeneficiars,
    fetchImportProvision,
    fetchImportPublicationsWithArticles,
    fetchImportSales,
    fetchImportStock,
    fetchImportTerms,
    fetchImportTitles,
    fetchImportWorks,
    getSignedURL,
    multipartUpload,
    resetImportModalState,
    revalidateImportSalesTable,
    setImportDataStatus,
    setPendingImports,
    uploadImportIndexFile,
    uploadToBucket,
    verifyImportSales,
    verifyImportTerms,
    verifyImportTitles,
} from "store/dataImportSlice";
import type { IFile, TFileMetadata } from "store/filesHistorySlice";
import { fetchImportedFiles } from "store/filesHistorySlice";
import { fetchInvoicePercentages } from "store/invoicePercentages";
import type { ImportJobStep } from "store/jobsStore";
import { ProcessingJobStatus } from "store/jobsStore";
import { getGeneralTabData } from "store/managementStore";
import { fetchPeriodTypes } from "store/periodStore";
import { fetchStocks } from "store/stockStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { exportTermList } from "store/termStore";
import { fetchTags, fetchTitles, importBatchSnapshots, verifyImportSubscriptionSnapshots } from "store/titleStore";
import { ChannelKind } from "store/types";
import { exportWorkList, fetchWorks } from "store/workStore";

import SendEmail from "../../assets/img/svg/sendEmail";
import { clearInvoice, createBexioInvoice, fetchBexioContacts } from "../../store/bexioStore";
import type { DTOBexioGETContact, ImportIndex, TImportTargets } from "../../types";
import { StyledMenuItem, StyledSelect } from "../shared/StyledSelect";
import DropZoneCSV from "../shared/UploadCsv";
import CorrectionPanel from "./CorrectionPanel";
import { CreateInvoiceModalContent } from "./CreateInvoiceModal";
import type {
    TImportAcceptedKeys,
    TImportConditions,
    TImportImmaterielKeys,
    TImportImmaterielTitles,
    TImportTitles,
} from "./import_data";
import { getBasicImportTypeData, getImportKeys, getImportTitles, getSingleValueCsvHeaders } from "./import_data";
import ImportStatusListener from "./ImportStatusListener";
import { showImportProgressToast } from "./ProgressToast";
import type { CurrentChannel, TCalculationMethodRender, TImportDataDictionary, TObj } from "./type";
import {
    advancePaymentTransform,
    articleTransform,
    balanceTransform,
    beneficiaryTransform,
    getElementCustomFields,
    getPublicationChannelPrice,
    pickMappedImportFields,
    withUnmappedColumns,
    provisionTransform,
    publicationTransform,
    publicationWithArticleTransform,
    salesTransform,
    formatImportErrorMessage,
    stockTransform,
    subscriptionTransform,
    termTransform,
    titleTransform,
} from "./utils";

interface IImportData {
    dictionaryKey: TImportDataDictionary;
    close: () => void;
    onSucceed?: (data: any) => void;
    moveToStep?: any;
    currentTab?: number;
    setCurrentTab?: any;
    titleId?: number;
    isPeriodClosed?: boolean;
}

export const inputLabelStyles = { fontFamily: "Poppins", fontWeight: "500", fontSize: "14px" };

export const borderValue = "1px solid rgba(224, 224, 224, 1)";

const ImportData = ({
    close,
    dictionaryKey,
    moveToStep,
    currentTab = 0,
    setCurrentTab,
    titleId,
    onSucceed,
    isPeriodClosed = false,
}: IImportData) => {
    const [t, i18n] = useTranslation();
    const dispatch = useAppDispatch();

    const portalType = useContext(PortalType);

    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [csvDelimiter, setCsvDelimiter] = useState<string>();

    const [importJobId, setImportJobId] = useState<number | null>(null);

    const { userData } = useAppSelector((state) => state.settings);
    const { channels, channelsStatus } = useAppSelector((state) => state.channels);
    const locations = useAppSelector((state) => state.stock.locations);
    const { activePeriod: activePeriodId, periods, periodTypes } = useAppSelector((state) => state.period);
    const { importDataStatus, importErrors, importErrorMessage, importPayloadLimit } = useAppSelector(
        (state) => state.dataImport,
    );
    const { bexioContacts, createdInvoice, failureReasonOnCreateInvoice } = useAppSelector((state) => state.bexio);
    const { invoicePercentages, invoicePercentagesStatus } = useAppSelector((state) => state.invoicePercentages);
    const { customFields } = useAppSelector((state) => state.customFields);
    const { hasBexioIntegration } = useAppSelector((state) => state.integrations);
    const { labels: calculationBaseColumnLabels, labelsStatus: calculationBaseColumnLabelsStatus } = useAppSelector(
        (state) => state.calculationBaseColumnLabels,
    );

    const { jobs } = useAppSelector((state) => state.jobs);
    const jobsLength = +(sessionStorage.getItem("jobs_length") ?? 0);
    const oldPendingImports = JSON.parse(sessionStorage.getItem("pending_imports") ?? "[]") as TPendingImport[];

    const [currentChannel, setCurrentChannel] = useState<CurrentChannel>({
        calculationMethod: 1,
        id: "",
    });
    const [currentLocation, setCurrentLocation] = useState<number | "">("");
    const [currentPeriodType, setCurrentPeriodType] = useState<number | "">("");
    const [provisionTarget, setProvisionTarget] = useState<"titles" | "terms">("titles");

    const [file, setFile] = useState<any>();
    const [dictKey, setDictKey] = useState<TImportDataDictionary>(dictionaryKey);

    useEffect(() => {
        if (dictionaryKey === "sales" && currentChannel.calculationMethod === CalculationMethod.PACKAGE) {
            setDictKey("forfait");
        }
    }, [dictionaryKey, currentChannel]);

    useEffect(() => {
        if (
            (dictKey === "sales" || dictKey === "titles" || dictKey === "forfait") &&
            calculationBaseColumnLabelsStatus === "idle"
        ) {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [dictKey, calculationBaseColumnLabelsStatus, dispatch]);

    const { getCompanySettings, companyCountryCode, countryFeatures, internationalBankingEnabled } =
        useCompanySettings();
    const usesMultiPeriods = getCompanySettings("uses_simultaneous_period");
    const usesOverridenPriceSales = getCompanySettings("uses_overriden_price_on_sales");
    const usesVat = getCompanySettings("uses_price_with_vat");
    const pressUsesBothChannels = getCompanySettings("press_uses_multiple_channels");
    const pressPreferredChannel = getCompanySettings("press_preferred_channel");
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const pressPreferredIdentifier = getCompanySettings("preferred_title_identifier");
    const usesCountryMarket = getCompanySettings("uses_market_segmentation");
    const usesAdvanceCompensation = getCompanySettings("uses_interterm_compensation");
    const usesTitleCompensation = getCompanySettings("uses_intertitle_compensation");
    const usesInitialAdvance = getCompanySettings("uses_initial_advance");
    const usesMultipleBrands = getCompanySettings("uses_multiple_brands");
    const usesAsyncUpload = getCompanySettings("uses_worker_mode");
    const usesDistributorSalesData = getCompanySettings("uses_distributor_sales_data");
    const usesCumulativeRoyalties = getCompanySettings("uses_cumulative_royalties");
    const allowsSubscriptionChannels = getCompanySettings("allows_subscription_channels");

    const isImmateriel = channels.find((item) => item.id === currentChannel.id)?.distributor === "Immateriel";
    const hasAtLeastOnePackageChannel = channels.some(
        (channel) => channel.calculationMethod === CalculationMethod.PACKAGE,
    );

    const currentCustomFields: ICustomField[] = getElementCustomFields(dictKey, customFields);

    const allConditions = useMemo(() => {
        let usesOverridenPrice = usesOverridenPriceSales;
        let usesBexio = hasBexioIntegration;

        if (currentChannel.id !== -1) {
            usesBexio = false; //Currently only Rosie uses Bexio and they always import through all channels.
            // Price override applies to proportional sales only — never to package lump sums.
            if (currentChannel.calculationMethod === CalculationMethod.PACKAGE) usesOverridenPrice = false;
        }

        const preferredChannel = channels.find((item) => item.id === +pressPreferredChannel);

        const customTurnoverLabels: TImportConditions["customTurnoverLabels"] = {};
        for (const row of calculationBaseColumnLabels) {
            customTurnoverLabels[row.calculationBaseColumn] = row.label;
        }

        const isCollectiveManagementChannel =
            currentChannel.id === -1
                ? channels.some((c) => c.kind === ChannelKind.COLLECTIVE_MANAGEMENT)
                : channels.find((c) => c.id === currentChannel.id)?.kind === ChannelKind.COLLECTIVE_MANAGEMENT;

        return {
            usesMultiPeriods,
            usesCountryMarket,
            usesOverridenPriceSales: usesOverridenPrice,
            usesMultipleCurrencies,
            hasBexioIntegration: usesBexio,
            usesAdvanceCompensation,
            usesTitleCompensation,
            usesISSN: pressPreferredIdentifier === "concat",
            pressUsesBothChannels,
            usesFolio: resolvePressChannelSlot(preferredChannel ?? { name: "", type: null }) === "folio",
            usesInitialAdvance,
            usesMultipleBrands,
            usesDirectPrecount:
                countryFeatures.precountMode === "direct" && countryFeatures.precount.options.length > 0,

            choseAllChannels: currentChannel?.id === -1,
            customFields: currentCustomFields,
            usesDistributorSalesData,
            usesCumulativeRoyalties,
            isProvisionAddedOnTitle: provisionTarget === "titles",
            allowsSubscriptionChannels,
            bankingFields: countryFeatures.bankingFields,
            urssaf: countryFeatures.urssaf,
            hasVat: countryFeatures.vat.options.length > 0,
            internationalBankingEnabled: !!internationalBankingEnabled,
            isCollectiveManagementChannel,
            customTurnoverLabels,
        };
    }, [channels, currentChannel, provisionTarget, calculationBaseColumnLabels]);

    const [calculationMethods] = useState<Array<TCalculationMethodRender>>([
        { id: "crealo", name: t("ImportData.CalculationCrealo") },
        { id: "file", name: t("ImportData.CalculationFile") },
    ]);

    const [invoiceCreatingStatus, setInvoiceCreatingStatus] = useState<"idle" | "pending" | "error" | "success">(
        "idle",
    );
    const [selectedBexioContact, setSelectedBexioContact] = useState<DTOBexioGETContact | null>(null);
    const [calculationMethodForInvoice, setCalculationMethodForInvoice] = useState<"crealo" | "file" | null>(null);

    const [fileErrors, setFileErrors] = useState<typeof importErrors>(importErrors ?? []);
    const [duplicateHeaders, setDuplicateHeaders] = useState<string[]>([]);
    const duplicateHeadersRef = useRef<string[]>([]);
    duplicateHeadersRef.current = duplicateHeaders;
    const [errorMessage, setErrorMessage] = useState(importErrorMessage ?? "");
    const [isRevalidating, setIsRevalidating] = useState(false);
    const [importJobFailed, setImportJobFailed] = useState(false);

    useEffect(() => {
        setFileErrors([]);
        setInvoiceCreatingStatus("idle");
        dispatch(clearInvoice());
        dispatch(resetImportModalState());

        // Cleanup: do NOT reset to idle if multipart/uploadImportIndexFile still in progress
        return () => {
            dispatch(resetImportModalState());
            dispatch(clearInvoice());
        };
    }, [dispatch]);

    useEffect(() => {
        if (importErrors.length > 0) {
            setFileErrors((prev) => [...prev, ...importErrors]);
        }
    }, [importErrors]);

    useEffect(() => {
        setErrorMessage(importErrorMessage);
    }, [importErrorMessage]);

    const isManagement = ["stock", "sales", "forfait"].includes(dictKey);

    const isProvision = dictKey === "provision";

    const hasMultiPeriods = usesMultiPeriods && dictKey === "terms";

    const isTitleKey = dictKey === "titles";
    const needsCalculationBaseLabels = dictKey === "sales" || dictKey === "titles" || dictKey === "forfait";
    const calculationBaseLabelsReady =
        !needsCalculationBaseLabels ||
        calculationBaseColumnLabelsStatus === "succeeded" ||
        calculationBaseColumnLabelsStatus === "failed";

    /**
     * Titles of our columns for each type.
     */
    const importTitles: TImportTitles = getImportKeys(dictKey, allConditions, "label", portalType);
    /**
     * Our variables for each type.
     */
    const importVariables: string[] = Object.keys(importTitles);
    /**
     * Keys accepted for each type: includes our variables and alternate keys.
     */
    const importAcceptedKeys: TImportAcceptedKeys = getImportKeys(dictKey, allConditions, "acceptedKeys", portalType);
    /**
     * Keys specific to Immateriel (only for sales).
     */
    const immaterielKeys: TImportImmaterielKeys =
        dictKey === "sales" ? getImportKeys(dictKey, allConditions, "immaterielKey", portalType) : [];
    /**
     * Titles specific to Immateriel (only for sales).
     */
    const immaterielTitles: TImportImmaterielTitles =
        dictKey === "sales" ? getImportKeys(dictKey, allConditions, "immaterielLabel", portalType) : [];

    /**
     * Either basic titles or immateriel titles depending on the case.
     */
    const allImportTitles: TImportTitles = useMemo(() => {
        let titles = {};
        if (isImmateriel) {
            Object.keys(immaterielTitles).map((key) => {
                titles[key] = immaterielTitles[key] as string;
            });
        } else {
            titles = importTitles;
        }

        return titles;
    }, [immaterielTitles, importTitles, isImmateriel]);

    const mandatoryKeysObject = getImportKeys(dictKey, allConditions, "mandatory", portalType) ?? {};
    /**
     * Keys that are mandatory for the import.
     */
    const mandatoryKeysArray: string[] = Object.keys(mandatoryKeysObject).filter(
        (key) => mandatoryKeysObject[key] === true,
    );

    const [array, setArray] = useState<any[]>([]);
    const [fileTitles, setFileTitles] = useState<string[]>([]);
    const [loadedData, setLoadedData] = useState<TObj[]>([]);
    const [selectValues, setSelectValue] = useState<TObj>({});
    const [notMatched, setNotMatched] = useState<null | number>(null);
    const [afterImportInfo, setAfterImportInfo] = useState<TImportDataSaleMetadata | null>(null);

    /**
     * Keys whose value accepts decimals, so their separator is normalized when the file is parsed.
     * The custom turnover columns are only part of the model when the company named them.
     */
    const floatKeys = useMemo(() => {
        const keys = [
            "channelPercentage_1",
            "channelPercentage_2",
            "channelPercentage_3",
            "channelPercentage_4",
            "channelPercentage_5",
            "channelMilestone_1",
            "channelMilestone_2",
            "channelMilestone_3",
            "channelMilestone_4",
            "channelMilestone_5",
            "initialBalance",
            "initialProvision",
            "amount",
            "turnover",
            "price",
            "packagePrice",
            "channelSubscriptionCoefficient",
        ];
        if (allConditions.customTurnoverLabels?.turnover_1) keys.push("turnover_1");
        if (allConditions.customTurnoverLabels?.turnover_2) keys.push("turnover_2");
        return keys;
    }, [allConditions.customTurnoverLabels]);

    const getLinkToDoc = (dictKey: TImportDataDictionary | "beneficiarsPress") => {
        if (dictKey === "beneficiarsPress")
            return "https://presse-helpdesk.crealo.app/fr/article/importer-des-beneficiaires-1rvo0lq/";
        return portalType && (getBasicImportTypeData(dictKey, portalType).relatedDoc as string);
    };

    const handlePreviousStep = () => {
        moveToStep(false)();
        setFileErrors([]);
        setErrorMessage("");
        dispatch(clearErrors());
        if (importDataStatus !== "idle") {
            dispatch(setImportDataStatus("idle"));
        }
    };

    const cleanModalOnClose = () => {
        setCurrentTab?.(0);
        setFileErrors([]);
        setLoadedData([]);
        setErrorMessage("");
        setFile(undefined);
        setArray([]);
        setFileTitles([]);
        setMapping({});
        setCsvDelimiter(undefined);
        setNotMatched(null);
        setSelectValue({});
        setImportJobId(null);
        setAfterImportInfo(null);
        setInvoiceCreatingStatus("idle");
        setSelectedBexioContact(null);
        setCalculationMethodForInvoice(null);
        setCurrentChannel({ calculationMethod: 1, id: "" });
        setCurrentLocation("");
        setCurrentPeriodType("");
        setProvisionTarget("titles");
        setImportJobFailed(false);
        dispatch(resetImportModalState());
        dispatch(clearInvoice());
    };

    const handleClose = () => {
        cleanModalOnClose();
        close();
    };

    const channelsLoading = ["idle", "pending"].includes(channelsStatus);

    const isAsyncUpload = usesAsyncUpload && ["beneficiars", "terms", "titles", "sales"].includes(dictKey);

    const filename = file?.length ? file[0].name : "";

    const closeRef = useRef(handleClose);
    closeRef.current = handleClose;

    const onStatusUpdate = useCallback(
        ({ status, step }: { status: ProcessingJobStatus; step: ImportJobStep; jobId: number }) => {
            if (step === "corrections-required") {
                setCurrentTab(7);
            }
            if (status === ProcessingJobStatus.FAILED || status === ProcessingJobStatus.REVERTED) {
                setImportJobFailed(true);
            }
        },
        [setCurrentTab],
    );

    const handleSalesImport = useCallback(async () => {
        if (!userData?.id) {
            return;
        }

        dispatch(setImportDataStatus("pending"));
        setCurrentTab(6);

        try {
            const payload = await dispatch(
                multipartUpload({ data: file[0], filename: file[0].name, method: "create", importType: "sales" }),
            ).unwrap();

            const importFileMeta: ImportIndex = {
                bucket: payload.bucket,
                fileKey: payload.fileKey,
                channelId: currentChannel.id === -1 ? undefined : currentChannel?.id,
                mapping,
                resourceType: "sales",
                uploadedBy: userData.id,
                delimiter: csvDelimiter || "",
                ...(payload.processingJobId && { processingJobId: payload.processingJobId }),
            };

            const importIndexUploadRes = await dispatch(
                uploadImportIndexFile({ data: importFileMeta, importType: "sales" }),
            ).unwrap();

            const importProcessingJobId = importIndexUploadRes.processingJobId;

            setImportJobId(importProcessingJobId);
        } catch (err) {
            // Bail out if the upload failed — otherwise we'd write an index file with no
            // fileKey and create a duplicate "started" job.
            // multipartUpload.rejected already set importDataStatus to "failed".
            return;
        }
    }, [dispatch, file, mapping, setCurrentTab, currentChannel, userData, csvDelimiter]);

    const handleAsyncUpload = async (data: any, type: TImportTargets) => {
        dispatch(setImportDataStatus("pending"));

        const URLResponse = await dispatch(getSignedURL({ method: "create", importType: type }));

        if (URLResponse.meta.requestStatus === "fulfilled") {
            const URL = URLResponse.payload.data;
            const response = await dispatch(uploadToBucket({ JSONData: data, filename, URL }));

            if (response.meta.requestStatus === "fulfilled") {
                dispatch(setImportDataStatus("succeeded"));

                const fakeImportData: Partial<IFile> & { importType: string } = {
                    filename,
                    date: new Date(),
                    importType: type,
                    target: type,
                    jobStatus: ProcessingJobStatus.PROCESSING,
                    user: userData?.email ?? "",
                    processingJobId: -1,
                    metadata: {} as TFileMetadata,
                    numberOfLines: data.length,
                };
                const newImports = [...oldPendingImports, fakeImportData];

                // Save updated pendingImports in session storage and store:
                sessionStorage.setItem("pending_imports", JSON.stringify(newImports));
                dispatch(setPendingImports(newImports));

                // Save new jobs length in storage:
                sessionStorage.setItem("jobs_length", ((jobsLength ? jobsLength : jobs.length) + 1).toString());

                // Create toast:
                toast.loading(`${t("AsyncUploads.PendingImports", { count: newImports.length })}`, {
                    closeButton: true,
                });
            }
        }
    };

    const makeRequest = useCallback(
        (type: TImportDataDictionary) => async () => {
            setFileErrors([]);
            setErrorMessage("");
            dispatch(clearErrors());
            dispatch(setImportDataStatus("pending"));
            const sourceRows = loadedData.filter((e) => !!Object.keys(e).length);
            const mappedRows = pickMappedImportFields(sourceRows, importVariables);
            if (["titles", "publications"].includes(type)) {
                const dataForVerify = mappedRows.map((item) => {
                    const next = { ...item };
                    if (typeof next.price === "string") {
                        next.price = next.price.replace(",", ".");
                    }
                    if (type === "publications") {
                        next.channelPrice = getPublicationChannelPrice(item);
                    } else if (Array.isArray(next.channelPrice)) {
                        next.channelPrice = next.channelPrice.map((e) => String(e).replace(",", "."));
                    } else if (typeof next.channelPrice === "string") {
                        next.channelPrice = next.channelPrice.replace(",", ".");
                    }
                    return next;
                });

                const data =
                    type === "publications"
                        ? publicationTransform(mappedRows)
                        : titleTransform({
                              data: mappedRows,
                              usesVat,
                              channels,
                              customFields: currentCustomFields,
                              companyCountryCode,
                          });

                if (isAsyncUpload) {
                    await handleAsyncUpload(data, "title");
                } else {
                    dispatch(verifyImportTitles({ data: dataForVerify as TObj[] })).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            dispatch(
                                fetchImportTitles({
                                    //@ts-expect-error originally it was just a ts-ignore added by dmitry, changed to expect-error for es-lint
                                    data,
                                    filename: file?.length ? file[0].name : "",
                                }),
                            ).then((resp) => {
                                if (resp.meta.requestStatus === "fulfilled") {
                                    dispatch(
                                        fetchTitles({
                                            limit: 100,
                                            sortBy: "creationDate",
                                            sortOrder: "DESC",
                                        }),
                                    );
                                    dispatch(fetchWorks(getWorksListParams()));
                                    dispatch(fetchTags());
                                    handleClose();
                                }
                            });
                        }
                    });
                }
            }
            if (type === "publicationsWithArticles") {
                const data = publicationWithArticleTransform(mappedRows, pressUsesBothChannels, pressPreferredChannel);
                dispatch(
                    fetchImportPublicationsWithArticles({
                        //@ts-expect-error originally it was just a ts-ignore added by dmitry, changed to expect-error for es-lint
                        data,
                        filename: file?.length ? file[0].name : "",
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        dispatch(fetchTitles({ limit: 100, sortBy: "creationDate", sortOrder: "DESC" }));
                        handleClose();
                        if (onSucceed) {
                            onSucceed(resp.payload[0]);
                        }
                    }
                });
            }
            if (type === "beneficiars") {
                const data = beneficiaryTransform({
                    data: mappedRows,
                    customFields: currentCustomFields,
                    bankingFields: countryFeatures.bankingFields,
                    internationalBankingEnabled,
                });

                if (isAsyncUpload) {
                    await handleAsyncUpload(data, "beneficiar");
                } else {
                    dispatch(
                        fetchImportBeneficiars({
                            data,
                            filename: file?.length ? file[0].name : "",
                        }),
                    ).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            dispatch(fetchBeneficiaries({ limit: 100 }));
                            dispatch(getBeneficiaryTags());
                            handleClose();
                        }
                    });
                }
            }

            if (type === "sales" && activePeriodId && typeof currentChannel.id === "number") {
                handleSalesImport();
                return;
            }

            if (["sales", "forfait"].includes(type) && activePeriodId && typeof currentChannel.id === "number") {
                const currentPeriods = periods.filter((item) => item.isActive === 1);
                if (currentPeriods.length > 0) {
                    const salesData = salesTransform(
                        mappedRows,
                        channels,
                        currentChannel,
                        currentPeriods,
                        usesMultiPeriods,
                        setFileErrors,
                    );

                    if (isAsyncUpload) {
                        await handleAsyncUpload(salesData, "sales");
                    } else {
                        // Check sale values before actually doing fetch:
                        const saleVerifyResponse = await dispatch(
                            verifyImportSales({
                                data: {
                                    date: currentPeriods.map((item) =>
                                        getCleanStartorEndDay(item.periodStart).toLocaleDateString("en-CA"),
                                    ),
                                    periodId: activePeriodId,
                                    channelId: currentChannel.id === -1 ? undefined : currentChannel.id,
                                    sales: salesData,
                                },
                                filename: file?.length ? file[0].name : "",
                            }),
                        );

                        if (saleVerifyResponse.meta.requestStatus === "fulfilled") {
                            const saleImportResponse = await dispatch(
                                fetchImportSales({
                                    data: {
                                        date: currentPeriods.map((item) =>
                                            getCleanStartorEndDay(item.periodStart).toLocaleDateString("en-CA"),
                                        ),
                                        periodId: activePeriodId,
                                        channelId: currentChannel.id === -1 ? undefined : currentChannel.id,
                                        sales: salesData,
                                    },
                                    filename: file?.length ? file[0].name : "",
                                }),
                            );

                            if (saleImportResponse.meta.requestStatus === "fulfilled") {
                                if (hasBexioIntegration && currentChannel.id === -1) {
                                    const { processingJobId, id } = saleImportResponse.payload;

                                    setAfterImportInfo({
                                        ...saleImportResponse.payload.metadata,
                                        processingJobId,
                                        id,
                                    } as TImportDataSaleMetadata);
                                    moveToStep(true);
                                    setCurrentTab((prev: number) => prev + 1);
                                } else {
                                    handleClose();
                                }
                            }
                        }
                    }
                }
            }
            if (type === "terms") {
                const data = termTransform(
                    withUnmappedColumns(
                        sourceRows,
                        mappedRows,
                        importVariables,
                        Object.values(importTitles),
                        Object.values(mapping),
                    ),
                    channels,
                );
                const periodTypeId = usesMultiPeriods ? +currentPeriodType : undefined;

                if (isAsyncUpload) {
                    await handleAsyncUpload(data, "terms");
                } else {
                    const verifyResponse = await dispatch(verifyImportTerms({ data, periodTypeId }));
                    if (verifyResponse.meta.requestStatus !== "fulfilled") {
                        return;
                    }

                    const importResponse = await dispatch(
                        fetchImportTerms({
                            data,
                            filename: file?.length ? file[0].name : "",
                            periodTypeId,
                        }),
                    );
                    if (importResponse.meta.requestStatus === "fulfilled" && activePeriodId) {
                        dispatch(
                            getGeneralTabData({
                                periodId: activePeriodId,
                                limit: 10,
                                sortOrder: "ASC",
                            }),
                        );
                        handleClose();
                    }
                }
            }
            if (type === "stock") {
                const stocks = stockTransform(mappedRows);
                dispatch(
                    fetchImportStock({
                        locationId: currentLocation,
                        stocks,
                        filename: file?.length ? file[0].name : "",
                        periodId: activePeriodId,
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        if (activePeriodId) {
                            dispatch(
                                fetchStocks({
                                    periodId: activePeriodId,
                                    limit: 10,
                                    sortOrder: "ASC",
                                }),
                            );
                            handleClose();
                        }
                    }
                });
            }
            if (type === "works") {
                const data = mappedRows;
                dispatch(
                    fetchImportWorks({
                        data,
                        filename: file?.length ? file[0].name : "",
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        dispatch(fetchWorks(getWorksListParams()));
                        handleClose();
                    }
                });
            }
            if (type === "advancePayments") {
                const data = advancePaymentTransform(mappedRows);
                dispatch(
                    fetchImportAdvancePayments({
                        data,
                        filename: file?.length ? file[0].name : "",
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        if (onSucceed) {
                            onSucceed(resp.payload);
                        } else {
                            dispatch(
                                fetchAllAdvanceSlices({
                                    options: {
                                        limit: 100,
                                        sortOrder: "desc",
                                        sortBy: "id",
                                        paidStatus: "all",
                                        isForfait: "all",
                                        periodId: activePeriodId,
                                    },
                                }),
                            );
                        }
                        handleClose();
                    }
                });
            }
            if (type === "articles") {
                const data = articleTransform(mappedRows, channels, pressUsesBothChannels, pressPreferredChannel);

                dispatch(
                    fetchImportArticles({
                        //@ts-expect-error originally it was just a ts-ignore added by dmitry, changed to expect-error for es-lint
                        titleId,
                        data,
                        filename: file?.length ? file[0].name : "",
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        if (onSucceed) {
                            onSucceed(resp.payload);
                        }
                        handleClose();
                    }
                });
            }
            if (type === "provision") {
                const { transformedData: data, fileErrors: transformErrors } = provisionTransform(mappedRows);

                if (transformErrors.length > 0) {
                    setFileErrors((prev) => [...prev, ...transformErrors]);
                    dispatch(setImportDataStatus("failed"));
                    return;
                }

                dispatch(
                    fetchImportProvision({
                        data,
                        filename: file?.length ? file[0].name : "",
                        periodId: activePeriodId,
                        target: provisionTarget,
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        if (activePeriodId) {
                            handleClose();
                        }
                    }
                });
            }
            if (type === "subscription") {
                if (!activePeriodId) {
                    dispatch(setImportDataStatus("idle"));
                    return;
                }
                const { transformedData: data, fileErrors: transformErrors } = subscriptionTransform(mappedRows);

                if (transformErrors.length > 0) {
                    setFileErrors((prev) => [...prev, ...transformErrors]);
                    dispatch(setImportDataStatus("failed"));
                    return;
                }

                const subscriptionChannels = channels.filter(
                    (channel) => channel.calculationMethod === CalculationMethod.SUBSCRIPTION,
                );
                const channelId = subscriptionChannels[0]?.id;
                if (typeof channelId !== "number") {
                    dispatch(setImportDataStatus("idle"));
                    return;
                }

                const titleSnapshots = data.map((item) => ({
                    ...(item.titleId ? { titleId: item.titleId as number } : { titleEan13: item.ean13 as string }),
                    channelId,
                    periodId: activePeriodId,
                    consumptionCount: item.consumptionCount as number,
                    avgConsumptionDuration: item.avgConsumptionDuration as number,
                }));

                const verifyResponse = await dispatch(
                    verifyImportSubscriptionSnapshots({
                        titleSnapshots,
                    }),
                );

                if (verifyResponse.meta.requestStatus === "fulfilled") {
                    const verifyPayload = verifyResponse.payload as any;

                    // Check if validation passed
                    if (
                        verifyPayload.valid === false &&
                        verifyPayload.errors &&
                        Array.isArray(verifyPayload.errors) &&
                        verifyPayload.errors.length > 0
                    ) {
                        // Map backend field names to CSV column names
                        const fieldMapping: Record<string, string> = {
                            titleEan13: "ean13",
                            titleId: "titleId",
                        };

                        // Format backend validation errors
                        const formattedErrors = verifyPayload.errors.map((err: any) => {
                            const originalField = fieldMapping[err.field] || err.field;
                            return {
                                entry: { [originalField]: err.value },
                                fileLine: (err.index || 0) + 2,
                                mappedKey: originalField,
                                message: err.message || "ImportData.InvalidData",
                            };
                        });

                        setFileErrors(() => formattedErrors);
                        dispatch(setImportDataStatus("failed"));
                        return;
                    }

                    dispatch(setImportDataStatus("pending"));

                    // Validation passed, proceed with import
                    dispatch(
                        importBatchSnapshots({
                            titleSnapshots,
                        }),
                    ).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") {
                            handleClose();
                        }
                    });
                } else if (verifyResponse.meta.requestStatus === "rejected") {
                    // Handle network/server errors
                    dispatch(setImportDataStatus("failed"));
                }
            }
            if (type === "balance") {
                if (!activePeriodId) {
                    dispatch(setImportDataStatus("idle"));
                    return;
                }
                const data = balanceTransform(mappedRows);
                dispatch(
                    bulkUpdateTermPeriodBalance({
                        data,
                        filename: file?.length ? file[0].name : "",
                        periodId: activePeriodId,
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        if (activePeriodId) {
                            handleClose();
                        }
                    }
                });
            }
        },
        [
            activePeriodId,
            handleClose,
            currentChannel,
            dispatch,
            loadedData,
            mapping,
            periods,
            importVariables,
            setCurrentTab,
            channels,
            currentLocation,
            file,
            titleId,
            handleSalesImport,
        ],
    );

    useEffect(() => {
        if (importDataStatus === "failed") {
            setLoadedData((prev) => prev.filter((e) => !!Object.keys(e).length));
        }
    }, [dispatch, importDataStatus]);

    useEffect(() => {
        if ((isManagement && currentTab === 2) || (!isManagement && currentTab === 1)) {
            setFile([]);
            setArray([]);
            setFileTitles([]);
            setLoadedData([]);
            setNotMatched(null);
            setSelectValue({});
        }
    }, [currentTab, isManagement]);

    useEffect(() => {
        if (loadedData.length === 0 && array.length !== 0) {
            setLoadedData(array);
        }
    }, [array, loadedData]);

    const rememberDuplicateHeaders = useCallback((duplicates: string[]) => {
        duplicateHeadersRef.current = duplicates;
        setDuplicateHeaders(duplicates);
        setArray([]);
        setFileTitles([]);
        setLoadedData([]);
    }, []);

    const clearDuplicateHeaders = useCallback(() => {
        duplicateHeadersRef.current = [];
        setDuplicateHeaders([]);
    }, []);

    const storeParsedImport = useCallback(
        (parsedFile: ParsedCsvFile) => {
            if (parsedFile.duplicateHeaders.length) {
                rememberDuplicateHeaders(parsedFile.duplicateHeaders);
                return;
            }

            clearDuplicateHeaders();
            setArray(parsedFile.loadedData);
            setFileTitles(parsedFile.fileTitles);
            setFileErrors([]);
            if (parsedFile.delimiter) setCsvDelimiter(parsedFile.delimiter);
            dispatch(clearErrors());
        },
        [clearDuplicateHeaders, dispatch, rememberDuplicateHeaders],
    );

    useEffect(() => {
        if (!file || file.length === 0) {
            clearDuplicateHeaders();
            return;
        }

        const usesRepeatedColumns = [
            "terms",
            "titles",
            "advancePayments",
            "beneficiars",
            "publications",
            "works",
        ].includes(dictKey);
        if (usesRepeatedColumns) {
            formatDataFromFile(
                {
                    file: file[0],
                    floatKeys,
                    singleValueHeaders: getSingleValueCsvHeaders(dictKey, allConditions, portalType),
                },
                storeParsedImport,
            );
            return;
        }
        parseCsvWithHeader({ file: file[0], floatKeys }, storeParsedImport);
    }, [allConditions, clearDuplicateHeaders, dictKey, file, floatKeys, portalType, storeParsedImport]);

    useEffect(() => {
        dispatch(fetchChannels());
        isManagement && hasBexioIntegration && dispatch(fetchBexioContacts());
    }, [dispatch]);

    useEffect(() => {
        if (hasBexioIntegration) {
            if (invoicePercentagesStatus === "idle") {
                dispatch(fetchInvoicePercentages());
            }
        }
    }, [hasBexioIntegration, invoicePercentagesStatus]);

    useEffect(() => {
        if (usesMultiPeriods) dispatch(fetchPeriodTypes());
    }, []);

    const displayChannelReminder = () => {
        if (currentChannel.id === -1) {
            return (
                <div className="import-data-channel-reminder">
                    <ChannelOther />
                    <span>{t("Channels.All")}</span>
                </div>
            );
        }

        if (channels && channels.length && !!currentChannel.id) {
            return (
                <div className="import-data-channel-reminder">
                    {channels.find((item) => item.id === currentChannel.id)?.kind === 1 ? (
                        <ChannelPhysique />
                    ) : channels.find((item) => item.id === currentChannel.id)?.kind === 2 ? (
                        <ChannelNumerique />
                    ) : channels.find((item) => item.id === currentChannel.id)?.kind === 3 ? (
                        <ChannelCession />
                    ) : channels.find((item) => item.id === currentChannel.id)?.kind === 4 ? (
                        <ChannelCollective />
                    ) : (
                        channels.find((item) => item.id === currentChannel.id)?.kind === 5 && <ChannelOther />
                    )}
                    <span>{channels.find((item) => item.id === currentChannel.id)?.name}</span>
                </div>
            );
        }
    };

    const handleDataModelExport = (key: TImportDataDictionary) => {
        let dictKey: TImportDataDictionary | "productions" | "services" | "productionsWithServices" = key;
        const examplesData = getImportTitles(key, allConditions, portalType);

        // If titles, we remaplce the price example with the right type (TTC or HT)
        // The example is always the second item of the examplesData array + the price is always on the 4th column.
        if (isTitleKey) {
            if (usesVat) {
                examplesData[1][3] = examplesData[1][3].replace(t("exclVAT"), t("VAT"));
            } else {
                examplesData[1][3] = examplesData[1][3].replace(t("VAT"), t("exclVAT"));
            }
        }

        if (portalType === "audiovisual") {
            if (key === "publications") dictKey = "productions";
            if (key === "articles") dictKey = "services";
            if (key === "publicationsWithArticles") dictKey = "productionsWithServices";
        }

        const csvEncoded = createEncodedCSVString(examplesData);
        const alink = document.createElement("a");
        alink.setAttribute("href", csvEncoded);
        alink.setAttribute("download", `file_model_import_${dictKey}.csv`);
        alink.click();
    };
    const handleWorksExport = () => dispatch(exportWorkList());
    const handleBeneficiarsExport = () => dispatch(exportBeneficiaryList());
    const handleChannelsExport = () => dispatch(exportChannelList());
    const handleTermsExport = () => dispatch(exportTermList());

    const handleSendInvoice = async () => {
        if (!selectedBexioContact) return;

        setInvoiceCreatingStatus("pending");

        try {
            if (!afterImportInfo || !afterImportInfo.processingJobId) {
                toast.error(`${t("ImportData.NoImportInfo")}`);
                throw new Error("No after import info available");
            }

            const response = await dispatch(
                createBexioInvoice({
                    contactId: selectedBexioContact.id,
                    processingJobId: afterImportInfo!.processingJobId,
                    importId: afterImportInfo!.id,
                    calculationMethodForInvoice: calculationMethodForInvoice!,
                }),
            );

            if (!(response.payload as any).id) {
                setInvoiceCreatingStatus("error");
                return;
            }

            setInvoiceCreatingStatus("success");
        } catch (e) {
            setInvoiceCreatingStatus("error");
            toast.error(`${t("Invoice.Error")}`);
            console.error(e);
        }
    };

    const getActionButton = () => {
        // the button is disable if there is no contact selected or if the invoice is pending
        const disabled =
            (!!afterImportInfo?.invoiceable && selectedBexioContact === null) || invoiceCreatingStatus === "pending";

        let text: string;
        let onClick: () => void;

        // If there is an invoice created and the status is success, the button is to see the invoice
        if (!isObjectEmpty(createdInvoice) && invoiceCreatingStatus === "success") {
            text = t("Invoice.See");
            onClick = () => {
                window.open(`https://office.bexio.com/index.php/kb_invoice/show/id/${createdInvoice.id}`, "_blank");
            };
        } else {
            // If there is no invoicable amount, or the invoice is already created, the button is to close the modal
            text = !afterImportInfo?.invoiceable ? t("Stock.Close") : t("Invoice.GenerateInvoice");
            // if there is a created invoice and the invoice is not pending, the choice is to close the modal
            if (!isObjectEmpty(createdInvoice) && invoiceCreatingStatus !== "pending") {
                onClick = handleClose;
            } else {
                onClick = afterImportInfo?.invoiceable ? handleSendInvoice : handleClose;
            }
        }

        return (
            <ImportModalButton
                variant="confirm"
                label={text}
                onClick={onClick}
                disabled={disabled}
                loading={invoiceCreatingStatus === "pending"}
                endIcon={
                    afterImportInfo?.invoiceable && invoiceCreatingStatus !== "success" ? <SendEmail /> : undefined
                }
            />
        );
    };

    const renderTemplateAndDoc = () => {
        const documentationLink =
            dictKey === "beneficiars" && portalType && ["audiovisual", "press"].includes(portalType)
                ? getLinkToDoc("beneficiarsPress")
                : getLinkToDoc(dictKey);

        return (
            <>
                {currentChannel.calculationMethod !== CalculationMethod.PACKAGE ? (
                    <div className="download-model" onClick={() => handleDataModelExport(dictKey)}>
                        <DocumentOutline />
                        {dictKey === "sales" && hasAtLeastOnePackageChannel ? (
                            <span>{t("ImportData.DownloadSalesTemplate")}</span>
                        ) : (
                            <span>{t("ImportData.DownloadModelData")}</span>
                        )}
                        <DownloadOutline />
                    </div>
                ) : (
                    <div className="download-model" onClick={() => handleDataModelExport("forfait")}>
                        <DocumentOutline />
                        <span>{t("ImportData.DownloadPackageTemplate")}</span>
                        <DownloadOutline />
                    </div>
                )}
                {documentationLink ? (
                    <DocumentationLink
                        title={t("ImportData.TitleForDoc")}
                        description={t("ImportData.SubtitleForDoc")}
                        link={documentationLink}
                    />
                ) : null}
            </>
        );
    };

    const allChannelsOption = {
        id: -1,
        name: t("Channels.All"),
        calculationMethod: CalculationMethod.PROPORTIONAL,
        kind: ChannelKind.OTHER,
    };

    const handleChooseChannel = (_e: SyntheticEvent<Element, Event>, channel: IChannel | null) => {
        if (!channel) return;
        setCurrentChannel({
            id: channel.id,
            calculationMethod: channel.calculationMethod,
        });
    };

    const asyncUploadSuccessful = importDataStatus === "succeeded" && isAsyncUpload;
    const displayedImportErrors = fileErrors.length > 0 ? fileErrors : importErrors;
    const errorLineIndexes = new Set(displayedImportErrors.map((e) => e.fileLine - 2));

    return (
        <>
            <div
                className={classNames("import-data-wrapper", {
                    "px-16!": currentTab !== 7,
                })}
            >
                <main className="relative import-data-main">
                    <TabContext value={currentTab.toString()}>
                        {["sales", "forfait"].includes(dictKey) && (
                            <TabPanel value="0" sx={{ margin: "20px 60px" }}>
                                <div className="import-data-main-choose-location">
                                    <div className="choose-location-field">
                                        <AutocompleteFormComponent
                                            label={t("ImportData.ChooseChannel")}
                                            options={
                                                isPeriodClosed
                                                    ? channels.filter((ch) =>
                                                          canAddSalesForKind(ch.kind as ChannelKind, true),
                                                      )
                                                    : [allChannelsOption, ...channels]
                                            }
                                            renderOption={(props, option) => renderChannelsOption(props, option)}
                                            renderOptionText={(channel) => channel.name}
                                            onChange={handleChooseChannel}
                                            defaultFilterOptions
                                            loading={channelsLoading}
                                        />
                                    </div>
                                </div>
                            </TabPanel>
                        )}
                        {dictKey === "stock" && (
                            <TabPanel value="0" sx={{ margin: "20px 60px" }}>
                                <div className="import-data-main-choose-location">
                                    <div className="choose-location-field">
                                        <InputLabel htmlFor="location" style={inputLabelStyles}>
                                            {t("ImportData.ChooseLocation")}
                                        </InputLabel>
                                        <StyledSelect name="location" fullWidth value={currentLocation}>
                                            {locations.map((location) => (
                                                <StyledMenuItem
                                                    onClick={() => setCurrentLocation(location.id)}
                                                    key={location.id}
                                                    value={location.id}
                                                >
                                                    {location.name}
                                                </StyledMenuItem>
                                            ))}
                                        </StyledSelect>
                                    </div>
                                </div>
                            </TabPanel>
                        )}
                        {dictKey === "terms" && usesMultiPeriods && (
                            <TabPanel value="0" sx={{ margin: "20px 60px" }} className="period-selection">
                                <InputLabel htmlFor="periodType">{t("ConnectTitle.PeriodType")}</InputLabel>
                                <StyledSelect name="periodType" fullWidth value={currentPeriodType}>
                                    {periodTypes.map((periodType) => {
                                        const fullPeriodData = periods.find(
                                            (item) => item.periodTypeId === periodType.id && item.isActive,
                                        );
                                        return (
                                            <StyledMenuItem
                                                onClick={() => setCurrentPeriodType(periodType.id)}
                                                key={periodType.id}
                                                value={periodType.id}
                                            >
                                                {periodType.duration} {t("ConnectTitle.PeriodTypeMonth")} -{" "}
                                                {formatLocaleDateString({ date: fullPeriodData?.periodStart ?? "" })}{" "}
                                                ↔︎ {formatLocaleDateString({ date: fullPeriodData?.periodEnd ?? "" })}
                                            </StyledMenuItem>
                                        );
                                    })}
                                </StyledSelect>
                            </TabPanel>
                        )}
                        {isProvision && (
                            <TabPanel
                                value="0"
                                sx={{ margin: "20px 60px" }}
                                className="period-selection flex! justify-center!"
                            >
                                <div className="w-[700px]!">
                                    <InputLabel htmlFor="provisionTarget">
                                        {t("ImportData.ProvisionTargetSelect")}
                                    </InputLabel>
                                    <StyledSelect name="provisionTarget" fullWidth value={provisionTarget}>
                                        <StyledMenuItem onClick={() => setProvisionTarget("titles")} value={"titles"}>
                                            {t("Titles.Titles")}
                                        </StyledMenuItem>
                                        <StyledMenuItem onClick={() => setProvisionTarget("terms")} value={"terms"}>
                                            {t("Titles.Terms")}
                                        </StyledMenuItem>
                                    </StyledSelect>
                                </div>
                            </TabPanel>
                        )}
                        <TabPanel value={isManagement || hasMultiPeriods || isProvision ? "1" : "0"}>
                            <div className="instructions">
                                <div className="instructions-information">
                                    <h2>
                                        <span>{t("ImportData.Instructions")}</span>
                                    </h2>
                                    <div className="instructions-subtitle">
                                        <span>{t("ImportData.InstructionsText")}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="examples">
                                {dictKey === "titles" && portalType === "book" ? (
                                    <div className="import-work-context">
                                        <p className="import-work-context__title">
                                            {t("ImportData.TitlesWorkContextTitle")}
                                        </p>
                                        <p className="import-work-context__text">
                                            {t("ImportData.TitlesWorkContextText")}
                                        </p>
                                        <div className="import-work-context__actions">{renderTemplateAndDoc()}</div>
                                    </div>
                                ) : (
                                    renderTemplateAndDoc()
                                )}

                                {dictKey === "terms" && (
                                    <>
                                        <hr style={{ border: "none" }} />
                                        <div className="download-model" onClick={() => handleBeneficiarsExport()}>
                                            <Beneficiaries />
                                            <span>{t("ImportData.DownloadBeneficiarsData")}</span>
                                            <DownloadOutline />
                                        </div>
                                        <div className="download-model" onClick={() => handleWorksExport()}>
                                            <Book />
                                            <span>{t("ImportData.DownloadWorksData")}</span>
                                            <DownloadOutline />
                                        </div>
                                        <div className="download-model" onClick={() => handleChannelsExport()}>
                                            <Channels />
                                            <span>{t("ImportData.DownloadChannelsData")}</span>
                                            <DownloadOutline />
                                        </div>
                                    </>
                                )}
                                {dictKey === "advancePayments" && (
                                    <>
                                        <hr style={{ border: "none" }} />
                                        <div className="download-model" onClick={() => handleTermsExport()}>
                                            <Beneficiaries />
                                            <span>{t("ImportData.DownloadTermsData")}</span>
                                            <DownloadOutline />
                                        </div>
                                    </>
                                )}
                                {(dictKey === "articles" || dictKey === "publicationsWithArticles") && (
                                    <>
                                        <hr style={{ border: "none" }} />
                                        <div className="download-model" onClick={() => handleBeneficiarsExport()}>
                                            <Beneficiaries />
                                            <span>{t("ImportData.DownloadBeneficiarsData")}</span>
                                            <DownloadOutline />
                                        </div>
                                    </>
                                )}
                            </div>
                        </TabPanel>
                        <TabPanel
                            value={isManagement || hasMultiPeriods || isProvision ? "2" : "1"}
                            sx={{ padding: "0" }}
                        >
                            {displayChannelReminder()}
                            <div>
                                <DropZoneCSV
                                    setFile={setFile}
                                    file={file}
                                    nextStep={() => {
                                        if (duplicateHeadersRef.current.length > 0) return;
                                        setCurrentTab((prevStep: number) => prevStep + 1);
                                    }}
                                    autoValidate
                                    // 50MB for sales, default for others
                                    maxSize={dictKey === "sales" ? 50 * 1000000 : undefined}
                                />
                                {duplicateHeaders.length > 0 && (
                                    <div className="missing-titles-message">
                                        <AlertTriangle />
                                        <span>
                                            {t("ImportData.Errors.DuplicateHeaders", {
                                                headers: duplicateHeaders.join(", "),
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                        <TabPanel
                            value={isManagement || hasMultiPeriods || isProvision ? "3" : "2"}
                            sx={{ paddingTop: 0 }}
                        >
                            {displayChannelReminder()}
                            <h1 className="mappage-columns-title">{t("ImportData.MappageColumns")}</h1>
                            {dictKey === "titles" && portalType === "book" && (
                                <p className="mappage-columns-hint">{t("ImportData.TitlesWorkContextMappingHint")}</p>
                            )}
                            {notMatched !== null && notMatched > 0 && (
                                <div className="missing-titles-message">
                                    <AlertTriangle />
                                    <span>
                                        {notMatched} {t("ImportData.NotMatched")}
                                    </span>
                                </div>
                            )}
                            <div className="table-wrapper">
                                {calculationBaseLabelsReady ? (
                                    <MappingColumns
                                        channelData={channels.find((item) => item.id === currentChannel.id)}
                                        isImmateriel={isImmateriel}
                                        importAcceptedKeys={isImmateriel ? immaterielKeys : importAcceptedKeys}
                                        importTitles={allImportTitles}
                                        dictionaryKey={dictKey}
                                        fileTitles={fileTitles}
                                        selectValues={selectValues}
                                        setNotMatched={setNotMatched}
                                        setSelectValue={setSelectValue}
                                        setLoadedData={setLoadedData}
                                        array={array}
                                        loadedData={loadedData}
                                        currentChannelId={currentChannel?.id}
                                        usesMultiPeriods={usesMultiPeriods}
                                        setMapping={setMapping}
                                        mandatoryKeys={mandatoryKeysArray}
                                    />
                                ) : (
                                    <div className="mapping-columns-pending">
                                        <Spinner size={48} />
                                    </div>
                                )}
                            </div>
                        </TabPanel>

                        <TabPanel
                            value={isManagement || hasMultiPeriods || isProvision ? "4" : "3"}
                            sx={{ paddingTop: 0 }}
                        >
                            {displayChannelReminder()}
                            {importDataStatus === "idle" && displayedImportErrors.length === 0 && (
                                <div className="import-modal-finish center">
                                    <span style={{ textAlign: "center" }}>
                                        <span style={{ backgroundColor: "#F4FDA9", padding: "0 4px" }}>
                                            {array.length}
                                        </span>
                                        {array.length > 1
                                            ? t("ImportData.ConfirmStartSeveral")
                                            : t("ImportData.ConfirmStart")}
                                        <span style={{ backgroundColor: "#F4FDA9", padding: "0 4px" }}>
                                            {file?.length && file[0].name}
                                        </span>
                                        {t("ImportData.ConfirmEnd")}
                                    </span>
                                </div>
                            )}
                            {importDataStatus === "pending" && displayedImportErrors.length === 0 && (
                                <div className="import-modal-finish center">
                                    <Spinner />
                                    <span style={{ marginTop: "1rem" }}>{t("ImportData.Pending")}</span>
                                    <span style={{ marginTop: "5px" }}>{t("ImportData.Pending2")}</span>
                                </div>
                            )}
                            {importDataStatus === "succeeded" &&
                                displayedImportErrors.length === 0 &&
                                (isAsyncUpload ? (
                                    <div className="import-modal-finish center">
                                        <span style={{ fontSize: "18px" }}>
                                            {t("ImportData.SuccessfullyImported1_Async", { count: loadedData.length })}
                                        </span>
                                        <span style={{ fontSize: "18px" }}>
                                            {t("ImportData.SuccessfullyImported2_Async")}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="import-modal-finish center">
                                        <span style={{ fontSize: "18px" }}>
                                            {loadedData.length}
                                            {t("ImportData.SuccessfullyImported")}
                                        </span>
                                    </div>
                                ))}
                            {importDataStatus === "failed" &&
                                (displayedImportErrors.length === 0 ? (
                                    <div className="import-modal-finish center">
                                        {importPayloadLimit ? (
                                            <span style={{ fontSize: "16px" }}>
                                                {t("ImportData.PayloadTooLarge", {
                                                    payloadSize: formatPayloadMegabytes(
                                                        importPayloadLimit.payloadBytes,
                                                        i18n.language,
                                                    ),
                                                    limitSize: formatPayloadMegabytes(
                                                        importPayloadLimit.limitBytes,
                                                        i18n.language,
                                                    ),
                                                })}
                                            </span>
                                        ) : (
                                            <>
                                                <span style={{ fontSize: "16px" }}>{t("ImportData.FileError")}</span>
                                                {errorMessage ? (
                                                    <span style={{ fontSize: "16px" }}>{t(errorMessage)}</span>
                                                ) : null}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    displayedImportErrors.length > 0 && (
                                        <div className="import-modal-finish center">
                                            {["titles", "terms", "works"].includes(dictKey) ? (
                                                <div style={{ width: "100%" }}>
                                                    <h1 className="mappage-columns-title">
                                                        {t("ImportData.Repairing")}
                                                    </h1>
                                                    <div className="missing-titles-message">
                                                        <AlertTriangle />
                                                        <span>
                                                            {displayedImportErrors.length + t("ImportData.ErrorsTerms")}
                                                        </span>
                                                    </div>
                                                    <div style={{ maxHeight: "450px", overflowY: "auto" }}>
                                                        {displayedImportErrors.map((err, indexError) => {
                                                            return (
                                                                <div
                                                                    key={indexError + "errorLines"}
                                                                    className="import-data-error-lines"
                                                                >
                                                                    <span>
                                                                        {t("ImportData.FileLine") + err.fileLine}
                                                                    </span>
                                                                    <span>
                                                                        {formatImportErrorMessage(err, importTitles)}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ width: "100%" }}>
                                                    <h1 className="mappage-columns-title">
                                                        {t("ImportData.Repairing")}
                                                    </h1>
                                                    <div className="missing-titles-message">
                                                        <AlertTriangle />
                                                        <span>
                                                            {
                                                                loadedData.filter(
                                                                    (d, i) =>
                                                                        errorLineIndexes.has(i) &&
                                                                        !!Object.keys(d).length,
                                                                ).length
                                                            }
                                                            {t("ImportData.RepairingTextStart")}
                                                            {!!array && array.length}
                                                            {t("ImportData.RepairingTextEnd")}
                                                        </span>
                                                    </div>
                                                    {!!importTitles && displayedImportErrors.length <= 100 && (
                                                        <>
                                                            <RepairingTable
                                                                templateKeys={importVariables}
                                                                importTitles={allImportTitles}
                                                                loadedData={loadedData}
                                                                setLoadedData={setLoadedData}
                                                                errors={displayedImportErrors}
                                                                mandatoryFields={
                                                                    [
                                                                        "publications",
                                                                        "publicationsWithArticles",
                                                                        "subscription",
                                                                    ].includes(dictKey)
                                                                        ? mandatoryKeysArray
                                                                        : undefined
                                                                }
                                                            />
                                                        </>
                                                    )}
                                                    {!!importTitles &&
                                                        displayedImportErrors.length > 100 &&
                                                        displayedImportErrors.map((err, indexError) => {
                                                            return (
                                                                <div
                                                                    key={indexError + "errorLines"}
                                                                    className="import-data-error-lines"
                                                                >
                                                                    <span>
                                                                        {t("ImportData.FileLine") + err.fileLine}
                                                                    </span>
                                                                    <span>
                                                                        {formatImportErrorMessage(err, importTitles)}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            )}
                                        </div>
                                    )
                                ))}
                        </TabPanel>

                        {/**
                         * This is the tab for the invoice
                         */}
                        {hasBexioIntegration && afterImportInfo && (
                            <TabPanel value={isManagement ? "5" : "4"}>
                                {/*invoice not created, creation not initiated*/}
                                {isObjectEmpty(createdInvoice) && invoiceCreatingStatus === "idle" && (
                                    <CreateInvoiceModalContent
                                        afterImportInfo={afterImportInfo}
                                        bexioContacts={bexioContacts}
                                        calculationMethods={calculationMethods}
                                        setSelectedBexioContact={setSelectedBexioContact}
                                        setTurnoverCalculationChoice={setCalculationMethodForInvoice}
                                        nbOfImportedLines={array.length}
                                        createdInvoice={createdInvoice}
                                        failureReasonOnCreateInvoice={failureReasonOnCreateInvoice}
                                        invoicePercentages={invoicePercentages}
                                    />
                                )}
                                {/*invoice not created, creation initiated*/}
                                {invoiceCreatingStatus === "pending" && (
                                    <div className="import-modal-finish center">
                                        <Spinner />
                                        <span style={{ marginTop: "1rem" }}>{t("Invoice.Pending")}</span>
                                    </div>
                                )}
                                {/*invoice created, creation finished*/}
                                {createdInvoice && invoiceCreatingStatus === "success" && (
                                    <div className="import-modal-finish center">
                                        <span className={"confirmed"} style={{ marginBottom: "1.5rem" }}>
                                            {t("Invoice.Created")} <br />
                                        </span>
                                    </div>
                                )}
                                {Object.keys(createdInvoice || {}).length === 0 &&
                                    invoiceCreatingStatus === "error" && (
                                        <div className="import-modal-finish center">
                                            <span style={{ fontSize: "14px" }}>
                                                {t("Invoice.Error")} : {failureReasonOnCreateInvoice ?? ""}
                                            </span>
                                        </div>
                                    )}
                            </TabPanel>
                        )}

                        {dictKey === "sales" && (
                            <TabPanel value="6">
                                <div className="h-[50vh]">
                                    <ImportStatusListener
                                        jobId={importJobId ?? undefined}
                                        onStatusUpdate={onStatusUpdate}
                                    />
                                </div>
                            </TabPanel>
                        )}

                        <TabPanel value="7" sx={{ paddingTop: 0 }}>
                            {importJobId ? (
                                <div className="h-[50vh]">
                                    {isRevalidating ? (
                                        <div className="h-full flex flex-col! items-center! justify-center! gap-2!">
                                            <Pending phrase="" />
                                            <div>{t("ImportData.ValidationInProgress")}</div>
                                        </div>
                                    ) : (
                                        <CorrectionPanel processingJobId={importJobId} contentType={dictKey} />
                                    )}
                                </div>
                            ) : null}
                        </TabPanel>
                    </TabContext>
                </main>
                <div className="buttons-wrapper">
                    {currentTab === 0 && (
                        <ImportModalButton
                            variant="continue"
                            onClick={moveToStep(true)}
                            disabled={
                                (["sales", "forfait"].includes(dictKey) && !currentChannel.id) ||
                                (hasMultiPeriods && currentPeriodType === "")
                            }
                        />
                    )}

                    {currentTab > 0 &&
                        currentTab < (isManagement ? 5 : 4) &&
                        importDataStatus !== "pending" &&
                        !asyncUploadSuccessful && <ImportModalButton variant="previous" onClick={handlePreviousStep} />}

                    {currentChannel.id === -1 &&
                        currentTab === (isManagement || hasMultiPeriods || isProvision ? 5 : 4) && (
                            <>
                                {!!afterImportInfo?.invoiceable && invoiceCreatingStatus !== "success" && (
                                    <ImportModalButton
                                        variant="ghost"
                                        label={t("Invoice.CloseBtn")}
                                        disabled={invoiceCreatingStatus === "pending"}
                                        onClick={handleClose}
                                    />
                                )}

                                {invoiceCreatingStatus === "success" && (
                                    <ImportModalButton variant="ghost" label={t("Stock.Close")} onClick={handleClose} />
                                )}

                                {getActionButton()}
                            </>
                        )}

                    {currentTab > 0 && currentTab < (isManagement || hasMultiPeriods || isProvision ? 4 : 3) ? (
                        <ImportModalButton
                            variant={
                                ((!isManagement && !hasMultiPeriods && !isProvision) || !hasMultiPeriods) &&
                                currentTab === 1
                                    ? "validate"
                                    : "continue"
                            }
                            disabled={
                                duplicateHeaders.length > 0 ||
                                (currentChannel.id === "" &&
                                    currentLocation === "" &&
                                    currentPeriodType === "" &&
                                    isManagement &&
                                    currentTab === 1) ||
                                (file?.length === 0 &&
                                    currentTab === (isManagement || hasMultiPeriods || isProvision ? 2 : 1)) ||
                                (currentTab === (isManagement || hasMultiPeriods || isProvision ? 3 : 2) &&
                                    (notMatched === null || notMatched > 0))
                            }
                            onClick={moveToStep(true)}
                        />
                    ) : currentTab === 6 && dictKey === "sales" && importJobFailed ? (
                        <ImportModalButton variant="close" onClick={handleClose} />
                    ) : currentTab === 7 && dictKey === "sales" ? (
                        <ImportModalButton
                            variant="retry"
                            loading={isRevalidating}
                            disabled={!importJobId}
                            onClick={() => {
                                if (importJobId) {
                                    setIsRevalidating(true);

                                    dispatch(revalidateImportSalesTable({ jobId: importJobId }))
                                        .then(({ meta, payload }) => {
                                            if (meta.requestStatus !== "fulfilled") {
                                                toast.error(`${t("ServerError.InternalServerError")}`);
                                                return;
                                            }
                                            const revalidationRes = payload.data as {
                                                status: ProcessingJobStatus;
                                                step: ImportJobStep;
                                            };

                                            if (revalidationRes.status === ProcessingJobStatus.PROCESSING) {
                                                showImportProgressToast(importJobId, dispatch);

                                                dispatch(fetchImportedFiles({ options: {} }));
                                                closeRef.current();
                                            }
                                        })
                                        .finally(() => {
                                            setIsRevalidating(false);
                                        });
                                }
                            }}
                        />
                    ) : (
                        currentTab < 6 &&
                        ![0, isManagement || hasMultiPeriods || isProvision ? 5 : 4].includes(currentTab) &&
                        (asyncUploadSuccessful ? (
                            <ImportModalButton variant="close" onClick={handleClose} />
                        ) : (
                            <ImportModalButton
                                variant={displayedImportErrors.length > 0 ? "retry" : "confirm"}
                                onClick={makeRequest(dictKey)}
                                loading={importDataStatus === "pending"}
                            />
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

export default ImportData;
